import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchOrganization, fetchOrganizations, type Organization } from '@/api/orgAdmin';
import type { OrganizationTreeNode } from './types';

/**
 * EAA-1818: the lazily-loaded `Organization.parent` tree behind
 * `/console/hierarchy`.
 *
 * WHY LAZY, AND WHY THIS SHAPE. portal-api exposes the hierarchy as one level
 * per request (`GET /organizations?parent_id=<uuid>`), and the view's own comment
 * states that is deliberate: "the console hierarchy screen loads one level at a
 * time". So there is no whole-tree endpoint to call and no recursive fetch to
 * write — a node's children are fetched the first time it is expanded, and never
 * again while they stay cached.
 *
 * That maps onto TanStack Query as `useQueries` over `[rootId, ...expandedIds]`:
 * one query per expanded parent, keyed `['org-children', token, parentId]`.
 * Collapsing a node drops it from the array but NOT from the query cache, so
 * re-expanding is instant and does not re-hit the network. A `useQuery` per node
 * inside the row component would achieve the same fetching but scatter the
 * invalidation surface across the tree; keeping every children query in one hook
 * is what lets a move invalidate exactly the two affected parents.
 *
 * ROOT DECISION (judgment call, stated explicitly because it is not the only
 * defensible one): the tree roots at the ACTING org, and shows only its
 * descendants — it does NOT walk up to the acting org's true root even now that
 * an org can have a real parent above it. Reasons: the whole console is scoped
 * by the acting-org picker in the topbar, every other console screen reads that
 * same scope, and surfacing ancestors would show the admin rows they cannot act
 * on while making "which org am I administering?" ambiguous. The acting org's
 * own `parent` is still fetched (see `rootOrgQuery`) because the cross-root
 * drag rule needs it — the tree just does not render upwards.
 */

/** Max children fetched per level. The endpoint pages; this is its ceiling. */
const CHILDREN_PAGE_SIZE = 100;

export interface OrgChildrenEntry {
    children: Organization[];
    loaded: boolean;
    loading: boolean;
    error: boolean;
    /** True when the level has more children than one page returned. */
    truncated: boolean;
}

export interface UseOrgTreeArgs {
    apiBase: string;
    accessToken: string | null;
    /** The acting organization's id — the tree's root (see the module doc). */
    rootOrgId: string | null;
    enabled: boolean;
}

export function useOrgTree({ apiBase, accessToken, rootOrgId, enabled }: UseOrgTreeArgs) {
    const queryClient = useQueryClient();
    const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

    /**
     * The root row itself. `useActingOrg()` only carries {id, code, name}
     * (`MeAdminOrg`), which is short of what the tree needs: `is_active` drives
     * the status dot and `parent` is required by the cross-root move rule, so
     * the full `Organization` row is fetched rather than synthesized.
     */
    const rootOrgQuery = useQuery({
        queryKey: ['organization', accessToken, rootOrgId],
        queryFn: () => fetchOrganization(apiBase, accessToken as string, rootOrgId as string),
        enabled: enabled && Boolean(rootOrgId),
        retry: false,
    });

    /**
     * The root starts expanded — a tree that opens as a single collapsed row
     * would hide the one level that is always worth fetching. It is seeded into
     * `expandedIds` rather than special-cased as `depth === 0` throughout, so its
     * twisty (frame 28 draws one on "EMG Hà Nội") is genuinely functional
     * instead of a decorative no-op, and "Thu gọn" can collapse it like any
     * other node.
     */
    useEffect(() => {
        if (rootOrgId) setExpandedIds(new Set([rootOrgId]));
    }, [rootOrgId]);

    /**
     * The root's children are fetched even while the root is collapsed: the
     * first level is what the screen exists to show, and keeping it warm makes
     * re-expanding instant. Every other level is fetched only once expanded.
     */
    const loadedParentIds = useMemo(() => {
        if (!rootOrgId) return [] as string[];
        return [rootOrgId, ...Array.from(expandedIds).filter((id) => id !== rootOrgId)];
    }, [rootOrgId, expandedIds]);

    const childrenQueries = useQueries({
        queries: loadedParentIds.map((parentId) => ({
            queryKey: ['org-children', accessToken, parentId],
            queryFn: () =>
                fetchOrganizations(apiBase, accessToken as string, {
                    parentId,
                    pageSize: CHILDREN_PAGE_SIZE,
                    ordering: 'name',
                }),
            enabled,
            retry: false,
        })),
    });

    const childrenByParent = useMemo(() => {
        const map = new Map<string, OrgChildrenEntry>();
        loadedParentIds.forEach((parentId, index) => {
            const query = childrenQueries[index];
            if (!query) return;
            const results = query.data?.results ?? [];
            map.set(parentId, {
                children: results,
                loaded: query.isSuccess,
                loading: query.isLoading,
                error: query.isError,
                truncated: (query.data?.count ?? 0) > results.length,
            });
        });
        return map;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadedParentIds, childrenQueries.map((q) => q.dataUpdatedAt + ':' + q.status).join('|')]);

    /**
     * Every organization row this hook has seen, by id. Feeds `resolveRootId`
     * for the best-effort cross-root check in `dragRules.ts`: that walk needs
     * `.parent` on each ancestor, and this is the only place the loaded rows are
     * collected in one lookup.
     */
    const knownOrgs = useMemo(() => {
        const map = new Map<string, Organization>();
        if (rootOrgQuery.data) map.set(rootOrgQuery.data.id, rootOrgQuery.data);
        for (const entry of childrenByParent.values()) {
            for (const org of entry.children) map.set(org.id, org);
        }
        return map;
    }, [rootOrgQuery.data, childrenByParent]);

    const tree = useMemo<OrganizationTreeNode[]>(() => {
        const rootOrg = rootOrgQuery.data;
        if (!rootOrg) return [];
        const build = (
            org: Organization,
            depth: number,
            seen: Set<string>
        ): OrganizationTreeNode => {
            const entry = childrenByParent.get(org.id);
            // `seen` stops a malformed parent cycle in the data from recursing
            // forever; the server guards against creating one, but a tree that
            // hard-locks the console on bad data is not an acceptable failure.
            const nextSeen = new Set(seen).add(org.id);
            const children = (entry?.children ?? [])
                .filter((child) => !nextSeen.has(child.id))
                .map((child) => build(child, depth + 1, nextSeen));
            return {
                id: org.id,
                organization: org,
                depth,
                children,
                childrenLoaded: entry?.loaded ?? false,
                childrenLoading: entry?.loading ?? false,
            };
        };
        return [build(rootOrg, 0, new Set())];
    }, [rootOrgQuery.data, childrenByParent]);

    /** Depth-first render order, stopping at collapsed nodes. */
    const visibleNodes = useMemo(() => {
        const out: OrganizationTreeNode[] = [];
        const walk = (nodes: OrganizationTreeNode[]) => {
            for (const node of nodes) {
                out.push(node);
                if (expandedIds.has(node.id)) walk(node.children);
            }
        };
        walk(tree);
        return out;
    }, [tree, expandedIds]);

    const toggleExpanded = useCallback((id: string) => {
        setExpandedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }, []);

    const expandNode = useCallback((id: string) => {
        setExpandedIds((prev) => (prev.has(id) ? prev : new Set(prev).add(id)));
    }, []);

    /**
     * EAA-1818: there is intentionally no "expand all". On a lazily-loaded tree
     * it would mean an unbounded fetch cascade — one request per node, depth
     * unknown up front — so the toolbar offers collapse only, matching frame
     * 28's single "Thu gọn" button.
     */
    const collapseAll = useCallback(() => setExpandedIds(new Set()), []);

    /**
     * Refetch exactly the levels a move touched: the old parent loses the node
     * and the new parent gains it. The moved org's OWN children query is
     * untouched on purpose — its subtree travels with it unchanged, so
     * invalidating it would re-fetch identical rows.
     */
    const invalidateParents = useCallback(
        (parentIds: Array<string | null>) => {
            for (const parentId of parentIds) {
                if (!parentId) continue;
                void queryClient.invalidateQueries({
                    queryKey: ['org-children', accessToken, parentId],
                });
            }
        },
        [queryClient, accessToken]
    );

    const invalidateOrg = useCallback(
        (orgId: string) => {
            void queryClient.invalidateQueries({ queryKey: ['organization', accessToken, orgId] });
        },
        [queryClient, accessToken]
    );

    const rootTruncated = childrenByParent.get(rootOrgId ?? '')?.truncated ?? false;

    return {
        rootOrgQuery,
        rootOrg: rootOrgQuery.data ?? null,
        tree,
        visibleNodes,
        knownOrgs,
        childrenByParent,
        rootTruncated,
        expandedIds,
        toggleExpanded,
        expandNode,
        collapseAll,
        invalidateParents,
        invalidateOrg,
        /** True while the first level is still in flight — drives the skeleton. */
        isLoadingRoot:
            rootOrgQuery.isLoading || (childrenByParent.get(rootOrgId ?? '')?.loading ?? false),
        isErrorRoot:
            rootOrgQuery.isError || (childrenByParent.get(rootOrgId ?? '')?.error ?? false),
    };
}

export type OrgTreeVM = ReturnType<typeof useOrgTree>;
